import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Check, Award, ArrowDown } from "lucide-react";
import { CREDIBILITY, PHONE, PHONE_HREF } from "./data/content";
import { scrollToId } from "../lib/scroll";
import { trackEvent } from "../lib/tracking";
import PhotoSlot from "./PhotoSlot";

const go = (id, location) => (e) => {
  e.preventDefault();
  trackEvent("cta_click", { location, target: id });
  scrollToId(id);
};

const LINES = [
  { text: "Build your own", accent: false },
  { text: "real estate business—", accent: true },
  { text: "without building", accent: false },
  { text: "the agency behind it.", accent: false },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  // subtle 3D tilt on the hero frame
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="hero" ref={ref} onMouseMove={onMove} className="relative overflow-hidden pt-[72px]">
      {/* ambient light */}
      <div className="pointer-events-none absolute -top-40 left-1/4 h-[560px] w-[560px] rounded-full bg-amber-600/15 blur-[140px]" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-cyan-500/8 blur-[120px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-24">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-2"
              data-testid="hero-award-badge"
            >
              <Award className="h-4 w-4 text-gold-light" />
              <span className="label-mono !text-gold-light !text-[10.5px]">
                REB &amp; RET 2026 · Boutique Network of the Year
              </span>
            </motion.div>

            <h1
              className="font-display text-[1.85rem] font-extrabold tracking-tight leading-[1.12] sm:text-5xl lg:text-[3rem] lg:leading-[1.1]"
              data-testid="hero-title"
            >
              {LINES.map((l, i) => (
                <span className="mask-line" key={i}>
                  <span style={{ animationDelay: `${0.15 + i * 0.12}s` }} className={l.accent ? "text-gold-light" : "text-white"}>
                    {l.text}
                  </span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
              data-testid="hero-subtitle"
            >
              Agency HQ gives experienced agents, teams and independent brands the systems, people and
              operational support to build a profitable real estate business. Operate under your own brand,
              co-brand with Agency HQ or use the Agency HQ name. You focus on relationships, listings and
              sales — we help manage everything behind the transaction.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <a
                href="#contact"
                onClick={go("contact", "hero")}
                data-testid="hero-primary-cta-btn"
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25 transition-all duration-200 hover:from-amber-600 hover:to-amber-700 active:scale-95"
              >
                Book a confidential conversation
              </a>
              <a
                href="#calculator"
                onClick={go("calculator", "hero")}
                data-testid="hero-secondary-cta-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-gold/50 hover:text-gold-light"
              >
                See what you could earn
                <ArrowDown className="h-4 w-4" />
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-4" style={{ perspective: "1200px" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
                <motion.div style={{ y: parallaxY }}>
                  <PhotoSlot
                    file="hero-team.jpg"
                    label="The Agency HQ team — drop your genuine team photo here (images/hero-team.jpg)"
                    aspect="aspect-[4/5] lg:aspect-[5/6]"
                    className="shadow-2xl shadow-black/50"
                  />
                </motion.div>
              </motion.div>
              <div
                className="relative z-10 -mt-10 ml-6 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-ink/90 px-5 py-4 backdrop-blur-xl"
                style={{ transform: "translateZ(50px)" }}
                data-testid="hero-stat-badge-commission"
              >
                <span className="font-display text-3xl font-extrabold text-gold-light">95%</span>
                <span className="text-xs leading-tight text-slate-300">
                  of your commission,
                  <br />
                  retained by you
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* credibility strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="border-t border-white/10 py-6"
          data-testid="hero-credibility-strip"
        >
          <ul className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {CREDIBILITY.map((c) => (
              <li key={c} className="flex items-center gap-2 text-[13px] font-medium text-slate-300">
                <Check className="h-4 w-4 shrink-0 text-gold-light" />
                {c}
              </li>
            ))}
          </ul>
        </motion.div>

        <div className="hidden pb-6 text-center lg:block">
          <a
            href={PHONE_HREF}
            className="text-xs font-mono text-slate-500 transition-colors hover:text-gold-light"
          >
            Prefer to talk? Call {PHONE}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
